import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import TumblerAuthBoundary from './TumblerAuthBoundary';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Divider, FormControlLabel,
  IconButton, MenuItem, Paper, Select, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { apiUrl } from '../../constants';
import ConfirmationDialog from './scheduling/components/ConfirmationDialog';
import QuantityWithUnit from './scheduling/components/QuantityWithUnit';
import normalizeApiError from './scheduling/utils/normalizeApiError';

// IMPLEMENTATION_PLAN.md Phase 1 + Phase 5 fixes to this page, per ASSESSMENT.md:
//  - P1: "Product Master ไม่มี absorb_percent" -> added, with a raw -> post-tumble kg preview,
//    since the backend uses this value for every demand calculation.
//  - P2: "Product Master save ใช้ Promise.all ทั้งชุด; delete ทันที" -> now saves only dirty
//    rows, one request each, with per-row results (a failure on one SKU no longer blocks or
//    re-submits the others); delete asks for confirmation first.
//  - P2: "ตาราง Product Master minWidth 2450" -> replaced the single 22-column table with a
//    compact list + a grouped detail form (Identity / Allergens / Capacity / Process steps /
//    Aging & expiry / Value), so units aren't crammed edge-to-edge.

const emptyProduct = () => ({
  id: null, line: '', sku: '', product_name: '', capacity_kg_hr: '', over_capacity: 1.5,
  raw_meat_time_min: 0, solution_time_min: 0, step_1_min: 0, step_2_min: 0, step_3_min: 0,
  step_4_min: 0, step_5_min: 0, step_6_min: 0, step_7_min: 0, total_tumbling_time_min: 0,
  mixing_time_min: 0, transfer_time_min: 0, aging_time_hr: 0, expire_time_days: '', product_value: '',
  absorb_percent: 0, allergens: [], is_active: true,
});

const numericFields = [
  'capacity_kg_hr', 'over_capacity', 'raw_meat_time_min', 'solution_time_min',
  'step_1_min', 'step_2_min', 'step_3_min', 'step_4_min', 'step_5_min', 'step_6_min', 'step_7_min',
  'total_tumbling_time_min', 'mixing_time_min', 'transfer_time_min', 'aging_time_hr',
  'expire_time_days', 'product_value', 'absorb_percent',
];

const STEP_FIELDS = ['step_1_min', 'step_2_min', 'step_3_min', 'step_4_min', 'step_5_min', 'step_6_min', 'step_7_min'];

function validateProduct(product) {
  const errors = {};
  if (!product.sku) errors.sku = 'Required';
  if (!product.product_name) errors.product_name = 'Required';
  if (product.absorb_percent !== '' && (Number(product.absorb_percent) < 0 || Number(product.absorb_percent) > 200)) {
    errors.absorb_percent = 'Expected 0–200%';
  }
  if (product.capacity_kg_hr !== '' && Number(product.capacity_kg_hr) < 0) errors.capacity_kg_hr = 'Must be ≥ 0';
  return errors;
}

export default function TumblerProductMaster() {
  return <TumblerAuthBoundary><ProductMasterContent /></TumblerAuthBoundary>;
}

function ProductMasterContent() {
  const { plant, factory } = useParams();
  const accessToken = useSelector((state) => state.auth.access_token);
  const [allergens, setAllergens] = useState([]);
  const [products, setProducts] = useState([]);
  const [dirtyIndexes, setDirtyIndexes] = useState(new Set());
  const [rowResults, setRowResults] = useState({}); // index -> { severity, text }
  const [newAllergen, setNewAllergen] = useState({ code: '', name: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const headers = { Authorization: `Bearer ${accessToken}` };

  const load = async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const [allergenResponse, productResponse] = await Promise.all([
        axios.get(apiUrl.TUMBLER_ALLERGEN_MASTER, { headers }),
        axios.get(`${apiUrl.TUMBLER_PRODUCT_MASTER}?plant_id=${encodeURIComponent(plant)}&factory=${encodeURIComponent(factory)}`, { headers }),
      ]);
      const allergenRows = Array.isArray(allergenResponse.data) ? allergenResponse.data : allergenResponse.data.results || [];
      const productRows = Array.isArray(productResponse.data) ? productResponse.data : productResponse.data.results || [];
      setAllergens(allergenRows);
      setProducts(productRows);
      setDirtyIndexes(new Set());
      setRowResults({});
      setSelectedIndex(null);
    } catch (error) {
      setMessage({ severity: 'error', text: normalizeApiError(error, 'Unable to load tumbler product master.').summary });
    } finally {
      setLoading(false);
    }
  };

  // Reset selection/dirty state whenever plant/factory scope changes, then reload for the new scope.
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [accessToken, plant, factory]);

  const updateProduct = (index, field, value) => {
    setProducts((current) => current.map((product, rowIndex) => (
      rowIndex === index ? { ...product, [field]: numericFields.includes(field) && value !== '' ? Number(value) : value } : product
    )));
    setDirtyIndexes((current) => new Set(current).add(index));
  };

  const addProduct = () => {
    setProducts((current) => {
      const next = [...current, emptyProduct()];
      setSelectedIndex(next.length - 1);
      setDirtyIndexes((dirty) => new Set(dirty).add(next.length - 1));
      return next;
    });
  };

  const saveDirtyProducts = async () => {
    const dirty = Array.from(dirtyIndexes);
    if (!dirty.length) {
      setMessage({ severity: 'info', text: 'No changes to save.' });
      return;
    }
    const validationFailures = dirty
      .map((index) => ({ index, errors: validateProduct(products[index]) }))
      .filter((entry) => Object.keys(entry.errors).length);
    if (validationFailures.length) {
      setRowResults((current) => {
        const next = { ...current };
        validationFailures.forEach(({ index, errors }) => {
          next[index] = { severity: 'error', text: `Fix before saving: ${Object.entries(errors).map(([f, m]) => `${f} (${m})`).join(', ')}` };
        });
        return next;
      });
      setMessage({ severity: 'error', text: `${validationFailures.length} row(s) need fixes before saving.` });
      return;
    }

    setSaving(true);
    setMessage(null);
    const nextResults = {};
    const stillDirty = new Set(dirtyIndexes);
    const updatedProducts = [...products];

    // Save each dirty row independently — one failure never blocks or retries the others.
    for (const index of dirty) {
      const product = updatedProducts[index];
      const payload = { ...product, plant_id: plant, factory };
      delete payload.id;
      try {
        const response = product.id
          ? await axios.patch(`${apiUrl.TUMBLER_PRODUCT_MASTER}${product.id}/`, payload, { headers })
          : await axios.post(apiUrl.TUMBLER_PRODUCT_MASTER, payload, { headers });
        updatedProducts[index] = response.data;
        nextResults[index] = { severity: 'success', text: 'Saved.' };
        stillDirty.delete(index);
      } catch (error) {
        nextResults[index] = { severity: 'error', text: normalizeApiError(error, 'Unable to save this product.').summary };
      }
    }

    setProducts(updatedProducts);
    setRowResults((current) => ({ ...current, ...nextResults }));
    setDirtyIndexes(stillDirty);
    setSaving(false);

    const failedCount = Object.values(nextResults).filter((r) => r.severity === 'error').length;
    if (failedCount) {
      setMessage({ severity: 'error', text: `${failedCount} of ${dirty.length} row(s) failed to save — see row-level messages.` });
    } else {
      setMessage({ severity: 'success', text: `${dirty.length} row(s) saved.` });
    }
  };

  const addAllergen = async () => {
    if (!newAllergen.code || !newAllergen.name) return;
    try {
      await axios.post(apiUrl.TUMBLER_ALLERGEN_MASTER, newAllergen, { headers });
      setNewAllergen({ code: '', name: '' });
      await load();
    } catch (error) {
      setMessage({ severity: 'error', text: normalizeApiError(error, 'Unable to add allergen.').summary });
    }
  };

  const requestRemove = (index) => {
    const product = products[index];
    if (!product.id) {
      setProducts((current) => current.filter((_, rowIndex) => rowIndex !== index));
      setDirtyIndexes((current) => { const next = new Set(current); next.delete(index); return next; });
      if (selectedIndex === index) setSelectedIndex(null);
      return;
    }
    setDeleteTarget({ index, product });
  };

  const confirmRemove = async () => {
    if (!deleteTarget) return;
    const { index, product } = deleteTarget;
    try {
      await axios.delete(`${apiUrl.TUMBLER_PRODUCT_MASTER}${product.id}/`, { headers });
      await load();
    } catch (error) {
      setMessage({ severity: 'error', text: normalizeApiError(error, 'Unable to remove product master.').summary });
    } finally {
      setDeleteTarget(null);
    }
  };

  const selectedProduct = selectedIndex != null ? products[selectedIndex] : null;
  const errors = selectedProduct ? validateProduct(selectedProduct) : {};

  return <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 4 }, py: 3 }}>
    <Helmet><title>Tumbler Product Master | MES</title></Helmet>
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
      <Box><Typography variant="h3">Tumbler Product Master</Typography><Typography color="text.secondary">Plant {plant} / Factory {factory}</Typography></Box>
      <Button variant="contained" startIcon={<SaveOutlinedIcon />} onClick={saveDirtyProducts} disabled={loading || saving}>
        {saving ? 'Saving…' : `Save changes${dirtyIndexes.size ? ` (${dirtyIndexes.size})` : ''}`}
      </Button>
    </Stack>
    {message && <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2 }}>{message.text}</Alert>}
    {loading ? <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 280 }}><CircularProgress /></Box> : <Stack spacing={3}>
      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h5">Allergen Master</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
          <TextField label="Allergen code" value={newAllergen.code} onChange={(event) => setNewAllergen((value) => ({ ...value, code: event.target.value }))} size="small" />
          <TextField label="Allergen name" value={newAllergen.name} onChange={(event) => setNewAllergen((value) => ({ ...value, name: event.target.value }))} size="small" fullWidth />
          <Button startIcon={<AddIcon />} onClick={addAllergen}>Add</Button>
        </Stack>
        <Typography sx={{ mt: 1.5 }} color="text.secondary">{allergens.map((allergen) => `${allergen.code}: ${allergen.name}`).join(' | ') || 'No allergens defined.'}</Typography>
      </Paper>

      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">SKUs</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addProduct}>Add SKU</Button>
        </Stack>
        <Box sx={{ overflowX: 'auto', mt: 2 }}>
          <Box component="table" sx={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', '& th, & td': { p: 0.75, borderBottom: '1px solid', borderColor: 'divider' }, '& th': { textAlign: 'left', fontWeight: 600 } }}>
            <thead><tr>{['Line', 'SKU', 'Product name', 'Active', 'Status', ''].map((label) => <th key={label}>{label}</th>)}</tr></thead>
            <tbody>
              {products.map((product, index) => (
                <tr
                  key={product.id ?? `new-${index}`}
                  onClick={() => setSelectedIndex(index)}
                  style={{ cursor: 'pointer', background: selectedIndex === index ? 'rgba(25,118,210,0.08)' : undefined }}
                >
                  <td>{product.line || '—'}</td>
                  <td>{product.sku || <em>(new)</em>}</td>
                  <td>{product.product_name || '—'}</td>
                  <td>{product.is_active ? 'Yes' : 'No'}</td>
                  <td>
                    {dirtyIndexes.has(index) && <Chip size="small" label="Unsaved" color="warning" sx={{ mr: 0.5 }} />}
                    {rowResults[index] && <Chip size="small" label={rowResults[index].text} color={rowResults[index].severity === 'error' ? 'error' : 'success'} />}
                  </td>
                  <td>
                    <Tooltip title="Remove SKU">
                      <IconButton size="small" onClick={(event) => { event.stopPropagation(); requestRemove(index); }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </td>
                </tr>
              ))}
              {!products.length && (
                <tr><td colSpan={6}><Typography color="text.secondary" sx={{ py: 2 }}>No SKUs yet. Add one to get started.</Typography></td></tr>
              )}
            </tbody>
          </Box>
        </Box>
      </Paper>

      {selectedProduct && (
        <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h5" sx={{ mb: 2 }}>{selectedProduct.sku || 'New SKU'} — detail</Typography>

          <Typography variant="subtitle1" fontWeight={600}>Identity</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1.5, mb: 3 }}>
            <TextField label="Line" value={selectedProduct.line || ''} onChange={(e) => updateProduct(selectedIndex, 'line', e.target.value)} size="small" />
            <TextField label="SKU" value={selectedProduct.sku || ''} onChange={(e) => updateProduct(selectedIndex, 'sku', e.target.value)} size="small" error={Boolean(errors.sku)} helperText={errors.sku} />
            <TextField label="Product name" value={selectedProduct.product_name || ''} onChange={(e) => updateProduct(selectedIndex, 'product_name', e.target.value)} size="small" fullWidth error={Boolean(errors.product_name)} helperText={errors.product_name} />
            <FormControlLabel control={<Checkbox checked={Boolean(selectedProduct.is_active)} onChange={(e) => updateProduct(selectedIndex, 'is_active', e.target.checked)} />} label="Active" />
          </Stack>

          <Typography variant="subtitle1" fontWeight={600}>Allergens</Typography>
          <Select
            multiple
            value={selectedProduct.allergens || []}
            onChange={(e) => updateProduct(selectedIndex, 'allergens', e.target.value)}
            size="small"
            sx={{ mt: 1.5, mb: 3, minWidth: 240 }}
          >
            {allergens.filter((a) => a.is_active).map((a) => <MenuItem key={a.id} value={a.id}>{a.code}: {a.name}</MenuItem>)}
          </Select>

          <Divider sx={{ mb: 3 }} />
          <Typography variant="subtitle1" fontWeight={600}>Capacity and value</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1.5, mb: 3 }}>
            <TextField label="Capacity (kg/hr)" type="number" value={selectedProduct.capacity_kg_hr ?? ''} onChange={(e) => updateProduct(selectedIndex, 'capacity_kg_hr', e.target.value)} size="small" error={Boolean(errors.capacity_kg_hr)} helperText={errors.capacity_kg_hr} />
            <TextField label="Over capacity factor" type="number" value={selectedProduct.over_capacity ?? ''} onChange={(e) => updateProduct(selectedIndex, 'over_capacity', e.target.value)} size="small" />
            <TextField label="Product value" type="number" value={selectedProduct.product_value ?? ''} onChange={(e) => updateProduct(selectedIndex, 'product_value', e.target.value)} size="small" />
          </Stack>

          <Divider sx={{ mb: 3 }} />
          <Typography variant="subtitle1" fontWeight={600}>Absorb % and expected yield</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1.5, mb: 1 }} alignItems="center">
            <TextField
              label="Absorb (%)"
              type="number"
              value={selectedProduct.absorb_percent ?? ''}
              onChange={(e) => updateProduct(selectedIndex, 'absorb_percent', e.target.value)}
              size="small"
              error={Boolean(errors.absorb_percent)}
              helperText={errors.absorb_percent || 'Weight gained during tumbling, as % of raw meat weight.'}
            />
            <Typography color="text.secondary">e.g. 1,000 kg raw at</Typography>
            <QuantityWithUnit value={selectedProduct.absorb_percent} unit="%" precision={1} />
            <Typography color="text.secondary">→ expected</Typography>
            <QuantityWithUnit
              value={1000 * (1 + (Number(selectedProduct.absorb_percent) || 0) / 100)}
              unit="kg post-tumble"
              precision={0}
            />
          </Stack>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" fontWeight={600}>Process steps (minutes)</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 2, mt: 1.5, mb: 3 }}>
            <TextField label="Raw meat" type="number" value={selectedProduct.raw_meat_time_min ?? ''} onChange={(e) => updateProduct(selectedIndex, 'raw_meat_time_min', e.target.value)} size="small" />
            <TextField label="Solution" type="number" value={selectedProduct.solution_time_min ?? ''} onChange={(e) => updateProduct(selectedIndex, 'solution_time_min', e.target.value)} size="small" />
            {STEP_FIELDS.map((field, i) => (
              <TextField key={field} label={`Step ${i + 1}`} type="number" value={selectedProduct[field] ?? ''} onChange={(e) => updateProduct(selectedIndex, field, e.target.value)} size="small" />
            ))}
            <TextField label="Total tumbling" type="number" value={selectedProduct.total_tumbling_time_min ?? ''} onChange={(e) => updateProduct(selectedIndex, 'total_tumbling_time_min', e.target.value)} size="small" />
            <TextField label="Mixing" type="number" value={selectedProduct.mixing_time_min ?? ''} onChange={(e) => updateProduct(selectedIndex, 'mixing_time_min', e.target.value)} size="small" />
            <TextField label="Transfer" type="number" value={selectedProduct.transfer_time_min ?? ''} onChange={(e) => updateProduct(selectedIndex, 'transfer_time_min', e.target.value)} size="small" />
          </Box>

          <Divider sx={{ mb: 3 }} />
          <Typography variant="subtitle1" fontWeight={600}>Aging and expiry</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1.5 }}>
            <TextField label="Aging (hr)" type="number" value={selectedProduct.aging_time_hr ?? ''} onChange={(e) => updateProduct(selectedIndex, 'aging_time_hr', e.target.value)} size="small" />
            <TextField label="Expire (days)" type="number" value={selectedProduct.expire_time_days ?? ''} onChange={(e) => updateProduct(selectedIndex, 'expire_time_days', e.target.value)} size="small" />
          </Stack>

          {rowResults[selectedIndex] && (
            <Alert severity={rowResults[selectedIndex].severity === 'error' ? 'error' : 'success'} sx={{ mt: 3 }}>
              {rowResults[selectedIndex].text}
            </Alert>
          )}
        </Paper>
      )}
    </Stack>}

    <ConfirmationDialog
      open={Boolean(deleteTarget)}
      title={`Remove ${deleteTarget?.product?.sku || 'this SKU'}?`}
      description="This deletes the product master row. Consider deactivating instead if it may still be referenced by past scenarios."
      confirmLabel="Delete"
      confirmColor="error"
      onCancel={() => setDeleteTarget(null)}
      onConfirm={confirmRemove}
    />
  </Box>;
}
